import { useCallback, useEffect, useState } from "react";

import lockupDark from "@/assets/logo/alpharoom-lockup-on-dark.svg";
import lockupLight from "@/assets/logo/alpharoom-lockup.svg";
import { Dialog } from "@/components/Dialog";

import {
  type Admin, type Customer, type CustomerDetail, type Health,
  type Invitation, type NewCustomer, type SmtpSettings, type SmtpTestResult,
  type NotificationEvent, type StaffMember, type StaffWithPassword,
  PlatformError, onUnauthorized, platform, restore, setToken,
} from "./api";

function relativeDays(days: number | null): string {
  if (days === null) return "no term set";
  if (days < 0) return `${Math.abs(days)} days ago`;
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days < 60) return `in ${days} days`;
  return `in ${Math.round(days / 30)} months`;
}

function licenceTone(state: Licence["state"]): string {
  return state === "active" ? "available"
    : state === "expiring" || state === "grace" ? "soon"
    : "occupied";
}
type Licence = Customer["licence"];

function message(caught: unknown, fallback = "That did not work."): string {
  return caught instanceof PlatformError ? caught.message : fallback;
}

/**
 * The mark, swapped by theme rather than recoloured — the same rule the
 * customer application follows. `Logo.tsx` itself cannot be reused here: it
 * reads the customer session, which this app deliberately does not have.
 */
function Lockup({ height = 26 }: { height?: number }) {
  const dark = document.documentElement.getAttribute("data-theme") === "dark";
  return (
    <img src={dark ? lockupDark : lockupLight} alt="AlphaRoom"
      height={height} style={{ height, width: "auto", display: "block" }} />
  );
}

// ------------------------------------------------------------------ sign in
function SignIn({ onDone }: { onDone: (admin: Admin) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = await platform.post<{ access_token: string; admin: Admin }>(
        "/auth/login", { email, password },
      );
      setToken(body.access_token);
      onDone(body.admin);
    } catch (caught) {
      setError(message(caught, "Sign-in failed."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page page--centred">
      <form className="card auth-card" onSubmit={submit}>
        <div className="card__body stack" style={{ gap: "var(--space-5)" }}>
          <div className="stack" style={{ gap: "var(--space-3)" }}>
            <Lockup height={30} />
            <div className="stack" style={{ gap: 4 }}>
              <h1 className="t-h2">Control plane</h1>
              <p className="t-body ink-secondary">
                Alphabeta staff only. Customers sign in on their own portal.
              </p>
            </div>
          </div>
          {error && <div className="notice notice--error">{error}</div>}
          <div className="field">
            <label className="field__label" htmlFor="email">Work e-mail</label>
            <input id="email" className="input" type="email" autoComplete="username"
              value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="password">Password</label>
            <input id="password" className="input" type="password"
              autoComplete="current-password" value={password}
              onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <button className="btn btn--primary" disabled={busy || !email || !password}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </div>
      </form>
    </div>
  );
}

// --------------------------------------------------------------- own account
function PasswordDialog({
  open, forced, onClose, onDone,
}: { open: boolean; forced: boolean; onClose: () => void; onDone: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await platform.post("/auth/password", {
        current_password: current, new_password: next,
      });
      setCurrent("");
      setNext("");
      onDone();
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      title="Change your password"
      description={forced
        ? "This account still has the password it was created with. Every other session for it ends when you change it."
        : "Every other session for this account ends when you change it."}
      onClose={onClose}
      dismissable={!forced}
      footer={
        <>
          {!forced && (
            <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
          )}
          <button className="btn btn--primary" onClick={submit}
            disabled={busy || !current || next.length < 12}>
            {busy ? "Saving…" : "Change password"}
          </button>
        </>
      }
    >
      <div className="stack" style={{ gap: "var(--space-4)" }}>
        {error && <div className="notice notice--error">{error}</div>}
        <div className="field">
          <label className="field__label" htmlFor="current">Current password</label>
          <input id="current" className="input" type="password"
            autoComplete="current-password" value={current}
            onChange={(e) => setCurrent(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="next">New password</label>
          <input id="next" className="input" type="password"
            autoComplete="new-password" value={next}
            onChange={(e) => setNext(e.target.value)} />
          <span className="field__hint">At least 12 characters.</span>
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------- invitations
function InvitationLink({ invitation }: { invitation: Invitation }) {
  const [copied, setCopied] = useState(false);
  if (!invitation.link) return null;
  return (
    <div className="field">
      <span className="field__label">Invitation link</span>
      <textarea className="input t-mono" readOnly rows={3} value={invitation.link}
        onFocus={(e) => e.currentTarget.select()} />
      <div className="row" style={{ gap: "var(--space-2)" }}>
        <button className="btn btn--secondary btn--compact" onClick={() => {
          navigator.clipboard?.writeText(invitation.link ?? "")
            .then(() => setCopied(true)).catch(() => undefined);
        }}>
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>
      <span className="field__hint">
        Expires {new Date(invitation.expires_at).toLocaleDateString()}. It is a
        credential: whoever holds it can set that person&rsquo;s password, and it
        works once.
      </span>
    </div>
  );
}

function invitationTone(state: Invitation["state"]): string {
  if (state === "redeemed") return "notice notice--success";
  if (state === "pending") return "notice notice--info";
  return "notice notice--warning";
}

function invitationSays(invitation: Invitation): string {
  const when = new Date(invitation.expires_at).toLocaleDateString();
  switch (invitation.state) {
    case "redeemed":
      return `${invitation.email} has signed in and set a password.`;
    case "pending":
      return `Waiting for ${invitation.email}. The link expires ${when}.`;
    case "expired":
      return `The link for ${invitation.email} expired on ${when}. Send a new one.`;
    default:
      return `The link for ${invitation.email} was revoked. Send a new one to let them in.`;
  }
}

// ----------------------------------------------------------------- onboarding
function OnboardDialog({
  open, onClose, onCreated,
}: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [timezone, setTimezone] = useState("Asia/Amman");
  const [termYears, setTermYears] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<NewCustomer | null>(null);

  function reset() {
    setName(""); setOwnerName(""); setOwnerEmail("");
    setTimezone("Asia/Amman"); setTermYears(1);
    setError(null); setCreated(null);
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const body = await platform.post<NewCustomer>("/customers", {
        name, owner_name: ownerName, owner_email: ownerEmail,
        timezone, term_years: termYears,
      });
      setCreated(body);
      onCreated();
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  }

  if (created) {
    return (
      <Dialog
        open={open}
        title={`${created.name} is set up`}
        description="Shown once."
        onClose={() => { reset(); onClose(); }}
        footer={
          <button className="btn btn--primary"
            onClick={() => { reset(); onClose(); }}>
            Done
          </button>
        }
      >
        <div className="stack" style={{ gap: "var(--space-4)" }}>
          <div className="notice notice--info">
            <strong>Send this link to the owner.</strong>
            <div className="t-caption">
              Nobody at Alphabeta is given a customer&rsquo;s password: they set
              their own from here. It has also been queued as an e-mail, which
              arrives only once a relay is configured — so until then this is
              the only copy, and it is shown once.
            </div>
          </div>
          <div className="field">
            <span className="field__label">Owner</span>
            <p className="t-body">{created.owner.name} · {created.owner.email}</p>
          </div>
          <InvitationLink invitation={created.invitation} />
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      title="Onboard a customer"
      description="Creates the organisation and its first owner. There is no serial number: the account is the licence."
      onClose={() => { reset(); onClose(); }}
      dismissable={false}
      footer={
        <>
          <button className="btn btn--ghost" onClick={() => { reset(); onClose(); }}>
            Cancel
          </button>
          <button className="btn btn--primary" onClick={submit}
            disabled={busy || name.trim().length < 2 || !ownerEmail || !ownerName}>
            {busy ? "Creating…" : "Create customer"}
          </button>
        </>
      }
    >
      <div className="stack" style={{ gap: "var(--space-4)" }}>
        {error && <div className="notice notice--error">{error}</div>}
        <div className="field">
          <label className="field__label" htmlFor="org">Organisation</label>
          <input id="org" className="input" value={name}
            placeholder="Northwind Group"
            onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="owner">Owner&rsquo;s name</label>
          <input id="owner" className="input" value={ownerName}
            placeholder="Layla Haddad"
            onChange={(e) => setOwnerName(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="ownerEmail">Owner&rsquo;s e-mail</label>
          <input id="ownerEmail" className="input" type="email" value={ownerEmail}
            placeholder="layla@northwind.com"
            onChange={(e) => setOwnerEmail(e.target.value)} />
          <span className="field__hint">
            They become the first administrator and can invite everyone else.
          </span>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="tz">Time zone</label>
          <input id="tz" className="input" value={timezone}
            onChange={(e) => setTimezone(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="term">Term</label>
          <select id="term" className="input" value={termYears}
            onChange={(e) => setTermYears(Number(e.target.value))}>
            <option value={1}>1 year</option>
            <option value={3}>3 years</option>
            <option value={0}>Nothing sold yet — internal or evaluation</option>
          </select>
          <span className="field__hint">
            Unlimited rooms and unlimited people either way.
          </span>
        </div>
      </div>
    </Dialog>
  );
}

// ----------------------------------------------------------------- customers
function CustomerList({
  customers, onOpen,
}: { customers: Customer[]; onOpen: (id: string) => void }) {
  if (customers.length === 0) {
    return (
      <div className="empty">
        <p className="empty__text">No customers yet.</p>
      </div>
    );
  }
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Customer</th><th>Term</th>
          <th>Subscription</th><th>Renews</th>
        </tr>
      </thead>
      <tbody>
        {customers.map((customer) => (
          <tr key={customer.id} onClick={() => onOpen(customer.id)}
            style={{ cursor: "pointer" }}>
            <td>
              <span className="t-label">{customer.name}</span>
              {!customer.active && (
                <span className="pill pill--occupied" style={{ marginInlineStart: 8 }}>
                  Suspended
                </span>
              )}
            </td>
            <td className="ink-secondary">
              {customer.term_years
                ? `${customer.term_years} year${customer.term_years === 1 ? "" : "s"}`
                : "—"}
            </td>
            <td>
              <span className={`pill pill--${licenceTone(customer.licence.state)}`}>
                {customer.licence.state}
              </span>
            </td>
            <td className="ink-secondary">
              {relativeDays(customer.licence.days_remaining)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Detail({
  id, canManage, onBack,
}: { id: string; canManage: boolean; onBack: () => void }) {
  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [notice, setNotice] = useState<string | null>(null);

  const [renewOpen, setRenewOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [freshLink, setFreshLink] = useState<Invitation | null>(null);
  const [newEmail, setNewEmail] = useState("");

  const load = useCallback(() => {
    platform.get<CustomerDetail>(`/customers/${id}`)
      .then(setCustomer)
      .catch((caught) => setError(String(caught.message)));
  }, [id]);
  useEffect(load, [load]);

  if (error) return <div className="notice notice--error">{error}</div>;
  if (!customer) return <p className="ink-tertiary">Loading…</p>;

  async function act(run: () => Promise<unknown>, ok: string) {
    setNotice(null);
    try {
      await run();
      setNotice(ok);
      load();
    } catch (caught) {
      setNotice(message(caught));
    }
  }

  const openSupport = () => act(
    () => platform.post(`/customers/${id}/support-session`, { reason, minutes: 60 })
      .then(() => setReason("")),
    "Support session opened. It is in their audit log with your name.",
  );

  const live = customer.support_sessions.filter((s) => !s.ended_at);

  return (
    <div className="stack" style={{ gap: "var(--space-5)" }}>
      <div className="row row--between">
        <button className="btn btn--ghost btn--compact" onClick={onBack}>
          ← All customers
        </button>
        {canManage && (
          <button
            className={customer.active ? "btn btn--danger btn--compact"
              : "btn btn--secondary btn--compact"}
            onClick={() => act(
              () => platform.post(
                `/customers/${id}/suspend?suspended=${customer.active}`,
              ),
              customer.active
                ? "Suspended. Their people cannot sign in."
                : "Reactivated.",
            )}
          >
            {customer.active ? "Suspend" : "Reactivate"}
          </button>
        )}
      </div>

      {notice && <div className="notice notice--info">{notice}</div>}

      <section className="card">
        <div className="card__header">
          <h2 className="t-h3">{customer.name}</h2>
          {!customer.active && <span className="pill pill--occupied">Suspended</span>}
        </div>
        <div className="card__body">
          <div className="stat-row">
            <div className="stat">
              <span className="stat__label">Rooms</span>
              <span className="stat__value">{customer.usage.rooms}</span>
              <span className="stat__note">unlimited</span>
            </div>
            <div className="stat">
              <span className="stat__label">People</span>
              <span className="stat__value">{customer.usage.users}</span>
            </div>
            <div className="stat">
              <span className="stat__label">Tablets</span>
              <span className="stat__value">{customer.usage.devices}</span>
            </div>
            <div className="stat">
              <span className="stat__label">Bookings</span>
              <span className="stat__value">{customer.usage.bookings_30d}</span>
              <span className="stat__note">last 30 days</span>
            </div>
          </div>
        </div>
      </section>

      {customer.invitation && (
        <section className="card">
          <div className="card__header">
            <h2 className="t-h3">Getting in</h2>
            {customer.invitation.sent_count > 1 && (
              <span className="t-caption ink-tertiary">
                sent {customer.invitation.sent_count} times
              </span>
            )}
          </div>
          <div className="card__body stack" style={{ gap: "var(--space-4)" }}>
            <div className={invitationTone(customer.invitation.state)}>
              {invitationSays(customer.invitation)}
            </div>

            {freshLink && <InvitationLink invitation={freshLink} />}

            {canManage && customer.invitation.state !== "redeemed" && (
              <>
                <div className="row" style={{ gap: "var(--space-2)" }}>
                  <button className="btn btn--secondary btn--compact"
                    onClick={() => act(
                      () => platform.post<Invitation>(
                        `/customers/${id}/invitation/resend`,
                      ).then(setFreshLink),
                      "A new link. The previous one stopped working.",
                    )}>
                    Issue a new link
                  </button>
                  {customer.invitation.state === "pending" && (
                    <button className="btn btn--ghost btn--compact"
                      onClick={() => act(
                        () => platform.post(`/customers/${id}/invitation/revoke`)
                          .then(() => setFreshLink(null)),
                        "Revoked. Nobody can use that link now.",
                      )}>
                      Revoke
                    </button>
                  )}
                </div>
                <div className="field">
                  <label className="field__label" htmlFor="fix-email">
                    Wrong address?
                  </label>
                  <div className="row" style={{ gap: "var(--space-2)" }}>
                    <input id="fix-email" className="input" type="email"
                      placeholder={customer.invitation.email} value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)} />
                    <button className="btn btn--secondary btn--compact"
                      disabled={!newEmail}
                      onClick={() => act(
                        () => platform.patch<Invitation>(
                          `/customers/${id}/invitation`, { email: newEmail },
                        ).then((body) => { setFreshLink(body); setNewEmail(""); }),
                        "Corrected. The owner account moved with it.",
                      )}>
                      Move it
                    </button>
                  </div>
                  <span className="field__hint">
                    The owner account moves too — leaving it behind would create
                    an owner who can never sign in.
                  </span>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      <section className="card">
        <div className="card__header">
          <h2 className="t-h3">Subscription</h2>
          {canManage && (
            <button className="btn btn--secondary btn--compact"
              onClick={() => setRenewOpen(true)}>
              Renew or extend
            </button>
          )}
        </div>
        <div className="card__body">
          <div className="stat-row">
            <div className="stat">
              <span className="stat__label">State</span>
              <span className={`pill pill--${licenceTone(customer.licence.state)}`}>
                {customer.licence.state}
              </span>
            </div>
            <div className="stat">
              <span className="stat__label">Term</span>
              <span className="stat__value">
                {customer.term_years ? `${customer.term_years}y` : "—"}
              </span>
            </div>
            <div className="stat">
              <span className="stat__label">Renews</span>
              <span className="stat__value">{customer.renews_at ?? "—"}</span>
              <span className="stat__note">
                {relativeDays(customer.licence.days_remaining)}
              </span>
            </div>
          </div>
          {customer.licence.message && (
            <p className="t-caption ink-secondary" style={{ marginBlockStart: "var(--space-3)" }}>
              {customer.licence.message}
            </p>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card__header">
          <h2 className="t-h3">Payments</h2>
          {canManage && (
            <button className="btn btn--secondary btn--compact"
              onClick={() => setPayOpen(true)}>
              Record a payment
            </button>
          )}
        </div>
        <div className="card__body">
          {customer.payments.length === 0 ? (
            <p className="ink-tertiary">Nothing recorded yet.</p>
          ) : (
            <table className="table">
              <thead>
                <tr><th>Paid</th><th>Amount</th><th>Reference</th><th>Covers until</th></tr>
              </thead>
              <tbody>
                {customer.payments.map((payment) => (
                  <tr key={payment.id}>
                    <td>{payment.paid_on}</td>
                    <td className="t-mono">
                      {payment.amount.toFixed(2)} {payment.currency}
                    </td>
                    <td className="ink-secondary">{payment.reference ?? "—"}</td>
                    <td className="ink-secondary">{payment.covers_until ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card__header">
          <div className="stack" style={{ gap: 2 }}>
            <h2 className="t-h3">Look at this account</h2>
            <span className="t-caption ink-tertiary">
              Time-boxed, and written into the customer&rsquo;s own audit log with
              your name and the reason you give. They will read it.
            </span>
          </div>
        </div>
        <div className="card__body stack" style={{ gap: "var(--space-3)" }}>
          {canManage && (
            <>
              <div className="field">
                <label className="field__label" htmlFor="reason">
                  Why do you need access?
                </label>
                <input id="reason" className="input" value={reason}
                  placeholder="Ticket 4471 — rooms missing after a Graph change"
                  onChange={(event) => setReason(event.target.value)} />
              </div>
              <button className="btn btn--secondary" disabled={reason.trim().length < 8}
                onClick={openSupport}>
                Open a one-hour session
              </button>
            </>
          )}

          {customer.support_sessions.length > 0 && (
            <table className="table">
              <thead><tr><th>Reason</th><th>Expires</th><th /></tr></thead>
              <tbody>
                {customer.support_sessions.map((session) => (
                  <tr key={session.id}>
                    <td>{session.reason}</td>
                    <td className="ink-secondary">
                      {session.ended_at ? "ended" : session.expires_at}
                    </td>
                    <td>
                      {!session.ended_at && canManage && (
                        <button className="btn btn--ghost btn--compact"
                          onClick={() => act(
                            () => platform.post(
                              `/customers/${id}/support-session/${session.id}/end`,
                            ),
                            "Session ended.",
                          )}>
                          End now
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {live.length > 0 && (
            <p className="t-caption ink-tertiary">
              {live.length} session{live.length === 1 ? "" : "s"} still open.
            </p>
          )}
        </div>
      </section>

      <RenewDialog open={renewOpen} customer={customer}
        onClose={() => setRenewOpen(false)}
        onDone={() => { setRenewOpen(false); setNotice("Subscription updated."); load(); }} />
      <PaymentDialog open={payOpen} customer={customer}
        onClose={() => setPayOpen(false)}
        onDone={() => { setPayOpen(false); setNotice("Payment recorded."); load(); }} />
    </div>
  );
}

function RenewDialog({
  open, customer, onClose, onDone,
}: {
  open: boolean; customer: CustomerDetail;
  onClose: () => void; onDone: () => void;
}) {
  const [termYears, setTermYears] = useState(customer.term_years ?? 1);
  const [renewsAt, setRenewsAt] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await platform.patch(`/customers/${customer.id}/subscription`, {
        term_years: termYears,
        renews_at: renewsAt || null,
      });
      onDone();
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      title="Renew or extend"
      description="Leave the date empty to run the term from today."
      onClose={onClose}
      dismissable={false}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" onClick={submit} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="stack" style={{ gap: "var(--space-4)" }}>
        {error && <div className="notice notice--error">{error}</div>}
        <div className="field">
          <label className="field__label" htmlFor="term-years">Term</label>
          <select id="term-years" className="input" value={termYears}
            onChange={(e) => setTermYears(Number(e.target.value))}>
            <option value={1}>1 year</option>
            <option value={3}>3 years</option>
            <option value={0}>Nothing sold — internal or evaluation</option>
          </select>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="renews">Renews on</label>
          <input id="renews" className="input" type="date" value={renewsAt}
            onChange={(e) => setRenewsAt(e.target.value)} />
          <span className="field__hint">
            Currently {customer.renews_at ?? "not set"}.
          </span>
        </div>
      </div>
    </Dialog>
  );
}

function PaymentDialog({
  open, customer, onClose, onDone,
}: {
  open: boolean; customer: CustomerDetail;
  onClose: () => void; onDone: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("JOD");
  const [paidOn, setPaidOn] = useState(today);
  const [reference, setReference] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await platform.post(`/customers/${customer.id}/payments`, {
        amount: Number(amount), currency, paid_on: paidOn,
        reference: reference || null,
      });
      setAmount("");
      setReference("");
      onDone();
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      title="Record a payment"
      description="A record of what was received. It does not change the subscription date on its own."
      onClose={onClose}
      dismissable={false}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" onClick={submit}
            disabled={busy || !(Number(amount) > 0)}>
            {busy ? "Saving…" : "Record"}
          </button>
        </>
      }
    >
      <div className="stack" style={{ gap: "var(--space-4)" }}>
        {error && <div className="notice notice--error">{error}</div>}
        <div className="row" style={{ gap: "var(--space-3)" }}>
          <div className="field grow">
            <label className="field__label" htmlFor="amount">Amount</label>
            <input id="amount" className="input" type="number" min="0" step="0.01"
              value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="currency">Currency</label>
            <input id="currency" className="input" maxLength={3} value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())} />
          </div>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="paid">Paid on</label>
          <input id="paid" className="input" type="date" value={paidOn}
            onChange={(e) => setPaidOn(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="ref">Reference</label>
          <input id="ref" className="input" value={reference}
            placeholder="Invoice 2026-014"
            onChange={(e) => setReference(e.target.value)} />
        </div>
      </div>
    </Dialog>
  );
}

// -------------------------------------------------------------------- health
function HealthPanel({ health }: { health: Health }) {
  if (health.quiet) {
    return (
      <div className="notice notice--success">
        Nothing needs attention across any customer.
      </div>
    );
  }
  const rows: { label: string; detail: string }[] = [
    ...health.failing_integrations.map((row) => ({
      label: `${row.organization} — ${row.provider} sync failing`,
      detail: `${row.consecutive_failures} failures. ${row.last_error}`,
    })),
    ...health.silent_tablets.map((row) => ({
      label: `${row.organization} — ${row.device} silent`,
      detail: row.never_seen
        ? "Never checked in; probably never mounted."
        : `Last seen ${row.last_seen_at}.`,
    })),
    ...health.expiring_subscriptions.map((row) => ({
      label: `${row.organization} — ${row.provider} subscription`,
      detail: row.awaiting_recreation
        ? "Removed by the provider; awaiting recreation."
        : `Expires ${row.expires_at}.`,
    })),
  ];
  return (
    <div className="stack" style={{ gap: "var(--space-3)" }}>
      {rows.map((row) => (
        <div key={row.label} className="notice notice--warning">
          <strong>{row.label}</strong>
          <div className="t-caption">{row.detail}</div>
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ settings
function SettingsPanel({ admin }: { admin: Admin }) {
  const [smtp, setSmtp] = useState<SmtpSettings | null>(null);
  const [form, setForm] = useState({
    host: "", port: 587, username: "", password: "",
    from_address: "", starttls: true,
  });
  const [to, setTo] = useState(admin.email);
  const [result, setResult] = useState<SmtpTestResult | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const adopt = useCallback((body: SmtpSettings) => {
    setSmtp(body);
    setForm({
      host: body.host ?? "", port: body.port, username: body.username ?? "",
      password: "", from_address: body.from_address, starttls: body.starttls,
    });
  }, []);

  useEffect(() => {
    platform.get<SmtpSettings>("/smtp").then(adopt).catch(() => undefined);
  }, [adopt]);

  async function save() {
    setSaving(true);
    setNotice(null);
    setResult(null);
    try {
      adopt(await platform.put<SmtpSettings>("/smtp", {
        host: form.host, port: form.port, username: form.username,
        password: form.password || null,
        from_address: form.from_address || null, starttls: form.starttls,
      }));
      setNotice("Saved. Send a test before you trust it.");
    } catch (caught) {
      setNotice(message(caught));
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    setBusy(true);
    setResult(null);
    try {
      setResult(await platform.post<SmtpTestResult>("/smtp/test", { to_address: to }));
    } catch (caught) {
      setResult({ ok: false, detail: message(caught) });
    } finally {
      setBusy(false);
    }
  }

  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });

  return (
    <div className="stack" style={{ gap: "var(--space-5)" }}>
      <section className="card">
        <div className="card__header">
          <div className="stack" style={{ gap: 2 }}>
            <h2 className="t-h3">Outgoing mail</h2>
            <span className="t-caption ink-tertiary">
              Every customer&rsquo;s mail is sent from this one address, because
              SPF, DKIM and DMARC can only be published on a domain we control.
              The customer&rsquo;s name goes in the display name instead.
            </span>
          </div>
        </div>
        <div className="card__body stack" style={{ gap: "var(--space-4)" }}>
          {!smtp ? <p className="ink-tertiary">Loading…</p> : (
            <>
              {!smtp.configured && (
                <div className="notice notice--warning">
                  <strong>No mail server is configured.</strong>
                  <div className="t-caption">
                    Nothing can be sent — invitations, alerts and weekly
                    summaries all queue up instead, and stay visible in the
                    customer&rsquo;s admin portal until a relay exists.
                  </div>
                </div>
              )}
              {smtp.configured && smtp.source === "environment" && (
                <div className="notice notice--info">
                  <strong>Coming from the server&rsquo;s .env.</strong>
                  <div className="t-caption">
                    Saving here takes over from it. Clearing the server field
                    hands it back.
                  </div>
                </div>
              )}
              {smtp.source === "database" && smtp.updated_at && (
                <p className="t-caption ink-tertiary">
                  Set by {smtp.updated_by ?? "somebody"} on{" "}
                  {new Date(smtp.updated_at).toLocaleDateString()}.
                </p>
              )}

              <div className="field">
                <label className="field__label" htmlFor="smtp-host">Server</label>
                <input id="smtp-host" className="input t-mono" value={form.host}
                  placeholder="email-smtp.eu-central-1.amazonaws.com"
                  disabled={!admin.can_manage}
                  onChange={(e) => set({ host: e.target.value })} />
                <span className="field__hint">
                  Amazon SES in Frankfurt is <span className="t-mono">
                  email-smtp.eu-central-1.amazonaws.com</span>, port 587,
                  STARTTLS on.
                </span>
              </div>
              <div className="row" style={{ gap: "var(--space-3)" }}>
                <div className="field" style={{ flex: "0 0 8rem" }}>
                  <label className="field__label" htmlFor="smtp-port">Port</label>
                  <input id="smtp-port" className="input t-mono" type="number"
                    value={form.port} disabled={!admin.can_manage}
                    onChange={(e) => set({ port: Number(e.target.value) })} />
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label className="field__label" htmlFor="smtp-user">Username</label>
                  <input id="smtp-user" className="input t-mono" value={form.username}
                    disabled={!admin.can_manage}
                    onChange={(e) => set({ username: e.target.value })} />
                </div>
              </div>
              <div className="field">
                <label className="field__label" htmlFor="smtp-pass">Password</label>
                <input id="smtp-pass" className="input" type="password"
                  autoComplete="new-password" value={form.password}
                  disabled={!admin.can_manage}
                  placeholder={smtp.password_set
                    ? "•••••••• stored — leave empty to keep"
                    : ""}
                  onChange={(e) => set({ password: e.target.value })} />
                <span className="field__hint">
                  For SES this is the SMTP credential it generates, not an IAM
                  access key. Stored encrypted and never shown again.
                </span>
              </div>
              <div className="field">
                <label className="field__label" htmlFor="smtp-from">Sends as</label>
                <input id="smtp-from" className="input t-mono" type="email"
                  value={form.from_address} disabled={!admin.can_manage}
                  onChange={(e) => set({ from_address: e.target.value })} />
                <span className="field__hint">
                  Must be an identity the relay has verified, on a domain whose
                  SPF and DKIM we publish.
                </span>
              </div>
              <label className="switch">
                <input type="checkbox" checked={form.starttls}
                  disabled={!admin.can_manage}
                  onChange={(e) => set({ starttls: e.target.checked })} />
                <span className="switch__track"><span className="switch__thumb" /></span>
                <span className="t-label">STARTTLS</span>
              </label>

              {admin.can_manage && (
                <div className="row" style={{ gap: "var(--space-2)" }}>
                  <button className="btn btn--primary" onClick={save} disabled={saving}>
                    {saving ? "Saving…" : "Save mail settings"}
                  </button>
                </div>
              )}
              {notice && <div className="notice notice--info">{notice}</div>}
            </>
          )}

          {smtp && (
            <div className="stack" style={{ gap: "var(--space-2)" }}>
              <span className="field__label">Bounce and complaint feedback</span>
              {smtp.feedback.topic_malformed && (
                <div className="notice notice--error">
                  <strong>SES_TOPIC_ARN is not a topic ARN.</strong>
                  <div className="t-caption">
                    The server has <span className="t-mono">{smtp.feedback.topic_arn}</span>.
                    A topic ARN reads{" "}
                    <span className="t-mono">arn:aws:sns:&lt;region&gt;:&lt;account&gt;:&lt;name&gt;</span>,
                    and nothing Amazon sends can match anything else, so every
                    bounce is refused. Copy it from the SNS console into the
                    server&rsquo;s .env, then run ops/deploy.sh.
                  </div>
                </div>
              )}
              {smtp.feedback.rejected ? (
                <div className="notice notice--error">
                  <strong>Amazon is reaching us and being turned away.</strong>
                  <div className="t-caption">
                    A correctly signed message arrived from a topic that
                    SES_TOPIC_ARN does not name, so it was refused — and every
                    real bounce will be refused the same way. Check that setting
                    in the server&rsquo;s .env against the topic&rsquo;s ARN in
                    the SNS console; a single wrong character does this. The last
                    one arrived{" "}
                    {new Date(smtp.feedback.last_seen_at!).toLocaleString()}.
                  </div>
                  {smtp.feedback.refused_topic && (
                    <div className="t-caption">
                      Amazon sent{" "}
                      <span className="t-mono">{smtp.feedback.refused_topic}</span>;
                      the server expects{" "}
                      <span className="t-mono">{smtp.feedback.topic_arn}</span>.
                    </div>
                  )}
                </div>
              ) : smtp.feedback.last_seen_at ? (
                <div className="notice notice--success">
                  <strong>Wired up.</strong>
                  <div className="t-caption">
                    Amazon has delivered {smtp.feedback.messages} message(s) here.
                    The last was “{smtp.feedback.last_kind}” on{" "}
                    {new Date(smtp.feedback.last_seen_at).toLocaleString()}.
                    {!smtp.feedback.topic_locked
                      && " SES_TOPIC_ARN is not set, so any correctly signed SNS topic is accepted."}
                  </div>
                </div>
              ) : (
                <div className="notice notice--warning">
                  <strong>Nothing has ever arrived here.</strong>
                  <div className="t-caption">
                    Either SNS was never pointed at this installation, or it was
                    pointed at it wrongly. An empty list of unreachable addresses
                    means nothing until this says otherwise. The endpoint to
                    subscribe is <span className="t-mono">{smtp.feedback.endpoint}</span>,
                    and the confirmation Amazon sends when you subscribe is
                    enough to light this up.
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="field">
            <label className="field__label" htmlFor="test-to">Send a test to</label>
            <input id="test-to" className="input" type="email" value={to}
              onChange={(e) => setTo(e.target.value)} />
            <span className="field__hint">
              Goes through exactly the path a customer notification takes. To
              exercise bounce handling without involving a real person, send to{" "}
              <span className="t-mono">bounce@simulator.amazonses.com</span> or{" "}
              <span className="t-mono">complaint@simulator.amazonses.com</span> —
              Amazon accepts them, generates a real bounce or complaint, and no
              mail reaches anybody.
            </span>
          </div>
          <button className="btn btn--secondary" onClick={sendTest}
            disabled={busy || !to || !admin.can_manage}>
            {busy ? "Sending…" : "Send test message"}
          </button>

          {result && (
            <div className={result.ok ? "notice notice--success" : "notice notice--error"}>
              {result.detail}
            </div>
          )}
        </div>
      </section>

      <EventSwitches admin={admin} />
    </div>
  );
}

// --------------------------------------------------- what the product sends
function EventSwitches({ admin }: { admin: Admin }) {
  const [events, setEvents] = useState<NotificationEvent[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    platform.get<{ events: NotificationEvent[] }>("/notification-events")
      .then((body) => setEvents(body.events)).catch(() => undefined);
  }, []);

  async function toggle(key: string, enabled: boolean) {
    if (!events) return;
    const next = events.map((e) => (e.key === key ? { ...e, enabled } : e));
    setBusy(true);
    setNotice(null);
    try {
      await platform.put("/notification-events", {
        disabled_event_keys: next.filter((e) => !e.enabled).map((e) => e.key),
      });
      setEvents(next);
      setNotice(enabled
        ? "On again for every customer."
        : "Off for every customer, whatever they have enabled.");
    } catch (caught) {
      setNotice(message(caught));
    } finally {
      setBusy(false);
    }
  }

  const off = events?.filter((e) => !e.enabled).length ?? 0;

  return (
    <section className="card">
      <div className="card__header">
        <div className="stack" style={{ gap: 2 }}>
          <h2 className="t-h3">What the product sends</h2>
          <span className="t-caption ink-tertiary">
            A customer can already switch any of these off for themselves. This
            is the other direction — off here is off for everybody, whatever
            they have enabled. Some events duplicate what a connected calendar
            already sends: Outlook confirms a booking, and a minute later so do
            we. Both are correct; together they read as noise and cost money on
            one sending identity.
          </span>
        </div>
        {off > 0 && <span className="pill pill--occupied">{off} off</span>}
      </div>
      <div className="card__body stack" style={{ gap: "var(--space-3)" }}>
        {notice && <div className="notice notice--info">{notice}</div>}
        {!events ? <p className="ink-tertiary">Loading…</p> : events.map((event) => (
          <label key={event.key} className="switch">
            <input type="checkbox" checked={event.enabled}
              disabled={busy || !admin.can_manage}
              onChange={(e) => toggle(event.key, e.target.checked)} />
            <span className="switch__track"><span className="switch__thumb" /></span>
            <span className="t-label">
              {event.label}
              <span className="ink-tertiary"> — {event.description}</span>
            </span>
          </label>
        ))}
      </div>
    </section>
  );
}

// --------------------------------------------------------------------- staff
function StaffDialog({
  open, onClose, onCreated,
}: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [canManage, setCanManage] = useState(true);
  const [created, setCreated] = useState<StaffWithPassword | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function close() {
    setEmail(""); setName(""); setCanManage(true);
    setCreated(null); setError(null);
    onClose();
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      setCreated(await platform.post<StaffWithPassword>("/staff", {
        email, display_name: name, can_manage: canManage,
      }));
      onCreated();
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  }

  if (created) {
    return (
      <Dialog open={open} title={`${created.display_name} can sign in`}
        description="Shown once." onClose={close}
        footer={<button className="btn btn--primary" onClick={close}>Done</button>}>
        <PasswordOnce admin={created} />
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      title="Add someone at Alphabeta"
      description="A control-plane account. It is not a user of any customer, and cannot become one."
      onClose={close}
      dismissable={false}
      footer={
        <>
          <button className="btn btn--ghost" onClick={close}>Cancel</button>
          <button className="btn btn--primary" onClick={submit}
            disabled={busy || !email || name.trim().length < 2}>
            {busy ? "Creating…" : "Create account"}
          </button>
        </>
      }
    >
      <div className="stack" style={{ gap: "var(--space-4)" }}>
        {error && <div className="notice notice--error">{error}</div>}
        <div className="field">
          <label className="field__label" htmlFor="staff-name">Name</label>
          <input id="staff-name" className="input" value={name}
            onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="staff-email">E-mail</label>
          <input id="staff-email" className="input" type="email" value={email}
            placeholder="name@alphabetajo.com"
            onChange={(e) => setEmail(e.target.value)} />
        </div>
        <label className="switch">
          <input type="checkbox" checked={canManage}
            onChange={(e) => setCanManage(e.target.checked)} />
          <span className="switch__track"><span className="switch__thumb" /></span>
          <span className="t-label">
            Can change things — subscriptions, customers, these settings
          </span>
        </label>
        <p className="t-caption ink-tertiary">
          Without it they can look at customers and open a support session, but
          not change what anyone pays.
        </p>
      </div>
    </Dialog>
  );
}

function PasswordOnce({ admin }: { admin: StaffWithPassword }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="stack" style={{ gap: "var(--space-4)" }}>
      <div className="notice notice--warning">
        <strong>This password is shown once.</strong>
        <div className="t-caption">
          Nothing here can e-mail it — the relay may be the very thing being
          configured. Pass it on yourself; they must change it at first sign-in.
        </div>
      </div>
      <div className="field">
        <span className="field__label">{admin.email}</span>
        <input className="input t-mono" readOnly value={admin.password}
          onFocus={(e) => e.currentTarget.select()} />
        <div className="row" style={{ gap: "var(--space-2)" }}>
          <button className="btn btn--secondary btn--compact" onClick={() => {
            navigator.clipboard?.writeText(admin.password)
              .then(() => setCopied(true)).catch(() => undefined);
          }}>
            {copied ? "Copied" : "Copy password"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StaffPanel({ admin }: { admin: Admin }) {
  const [rows, setRows] = useState<StaffMember[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [reset, setReset] = useState<StaffWithPassword | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    platform.get<{ staff: StaffMember[] }>("/staff")
      .then((body) => setRows(body.staff)).catch(() => undefined);
  }, []);
  useEffect(load, [load]);

  async function act(run: () => Promise<unknown>, ok: string) {
    setNotice(null);
    try {
      await run();
      setNotice(ok);
      load();
    } catch (caught) {
      setNotice(message(caught));
    }
  }

  return (
    <div className="stack" style={{ gap: "var(--space-4)" }}>
      <div className="row row--between">
        <p className="t-caption ink-tertiary">
          Everyone at Alphabeta who can open this portal. A control-plane
          session reaches every customer&rsquo;s data, so this list should be
          short and its leavers should be deactivated the day they leave.
        </p>
        {admin.can_manage && (
          <button className="btn btn--primary btn--compact"
            onClick={() => setAddOpen(true)}>
            Add someone
          </button>
        )}
      </div>

      {notice && <div className="notice notice--info">{notice}</div>}

      <table className="table">
        <thead>
          <tr>
            <th>Person</th><th>Access</th><th>Last signed in</th><th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className={row.active ? undefined : "is-muted"}>
              <td>
                <div className="stack" style={{ gap: 2 }}>
                  <span>{row.display_name}{row.id === admin.id && " · you"}</span>
                  <span className="t-caption ink-tertiary t-mono">{row.email}</span>
                </div>
              </td>
              <td>
                {!row.active ? <span className="pill">Deactivated</span>
                  : row.can_manage ? <span className="pill pill--available">Full</span>
                    : <span className="pill">Read only</span>}
                {row.must_change_password && row.active && (
                  <div className="t-caption ink-tertiary">
                    still has its first password
                  </div>
                )}
              </td>
              <td className="t-caption">
                {row.last_login_at
                  ? new Date(row.last_login_at).toLocaleDateString()
                  : "never"}
              </td>
              <td>
                {admin.can_manage && (
                  <div className="row" style={{ gap: "var(--space-2)" }}>
                    {row.active && (
                      <button className="btn btn--ghost btn--compact"
                        onClick={() => act(
                          () => platform.post<StaffWithPassword>(
                            `/staff/${row.id}/password`,
                          ).then(setReset),
                          "New password issued. Every session it had has ended.",
                        )}>
                        Reset password
                      </button>
                    )}
                    <button className="btn btn--ghost btn--compact"
                      onClick={() => act(
                        () => platform.patch(`/staff/${row.id}`,
                          { active: !row.active }),
                        row.active
                          ? "Deactivated. Their sessions ended immediately."
                          : "Reactivated.",
                      )}>
                      {row.active ? "Deactivate" : "Reactivate"}
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <StaffDialog open={addOpen} onClose={() => setAddOpen(false)}
        onCreated={load} />
      <Dialog open={reset !== null} title="New password"
        description="Shown once." onClose={() => setReset(null)}
        footer={
          <button className="btn btn--primary" onClick={() => setReset(null)}>
            Done
          </button>
        }>
        {reset && <PasswordOnce admin={reset} />}
      </Dialog>
    </div>
  );
}

// ----------------------------------------------------------------------- app
export function PlatformApp() {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [health, setHealth] = useState<Health | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  type Tab = "customers" | "health" | "staff" | "settings";
  const TABS: Tab[] = ["customers", "health", "staff", "settings"];

  // The portal has no router — it is four tabs — but the address bar should
  // still say where you are. Without this every tab read /sign-in, which is
  // both wrong and alarming. replaceState rather than pushState: the tabs are
  // not history, and Back should leave the portal rather than walk them.
  const pathTab = (): Tab => {
    const first = window.location.pathname.replace(/^\/+/, "").split("/")[0];
    return (TABS as string[]).includes(first) ? (first as Tab) : "customers";
  };
  const [tab, setTabState] = useState<Tab>(pathTab);
  const setTab = useCallback((next: Tab) => {
    setTabState(next);
    window.history.replaceState(null, "", `/${next}`);
  }, []);

  const [restoring, setRestoring] = useState(true);
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  useEffect(() => {
    onUnauthorized(() => setAdmin(null));
    // Reloading the page used to sign you out. The refresh cookie survives it.
    restore()
      .then((restored) => restored && setAdmin(restored))
      .finally(() => setRestoring(false));
  }, []);

  const refresh = useCallback(() => {
    if (!admin) return;
    platform.get<{ customers: Customer[] }>("/customers")
      .then((body) => setCustomers(body.customers)).catch(() => undefined);
    platform.get<Health>("/health").then(setHealth).catch(() => undefined);
  }, [admin]);
  useEffect(refresh, [refresh]);

  // Without this the sign-in form flashes up for a moment on every reload
  // before the restored session replaces it.
  if (restoring) {
    return (
      <div className="page platform__body">
        <p className="ink-tertiary">Restoring your session…</p>
      </div>
    );
  }
  if (!admin) return <SignIn onDone={setAdmin} />;

  const attention = customers.filter(
    (c) => c.licence.state !== "active" || !c.active,
  );
  const forced = admin.must_change_password;

  return (
    <div className="page">
      <header className="platform__header">
        <div className="row" style={{ gap: "var(--space-4)", alignItems: "center" }}>
          <Lockup />
          <div className="stack" style={{ gap: 2 }}>
            <span className="t-label">Control plane</span>
            <span className="t-caption ink-tertiary">
              {admin.display_name}{!admin.can_manage && " · read only"}
            </span>
          </div>
        </div>
        <div className="row" style={{ gap: "var(--space-2)" }}>
          <button className="btn btn--ghost btn--compact"
            onClick={() => setPasswordOpen(true)}>
            Password
          </button>
          <button className="btn btn--ghost btn--compact" onClick={() => {
            platform.post("/auth/logout").catch(() => undefined);
            setToken(null);
            setAdmin(null);
            window.history.replaceState(null, "", "/");
          }}>
            Sign out
          </button>
        </div>
      </header>

      <main className="platform__body stack" style={{ gap: "var(--space-5)" }}>
        {forced && (
          <div className="notice notice--warning">
            <strong>This account still has the password it was created with.</strong>
            <div className="t-caption">
              <button className="btn btn--ghost btn--compact"
                onClick={() => setPasswordOpen(true)}>
                Change it now
              </button>
            </div>
          </div>
        )}

        {attention.length > 0 && tab === "customers" && (
          <div className="notice notice--warning">
            {attention.length} customer{attention.length === 1 ? "" : "s"} need
            {attention.length === 1 ? "s" : ""} attention — listed first below.
          </div>
        )}

        <div className="row row--between">
          <div className="segmented" role="group" aria-label="Section">
            <button type="button" aria-pressed={tab === "customers"}
              onClick={() => { setTab("customers"); setOpen(null); }}>
              Customers
            </button>
            <button type="button" aria-pressed={tab === "health"}
              onClick={() => setTab("health")}>
              Health
            </button>
            <button type="button" aria-pressed={tab === "staff"}
              onClick={() => setTab("staff")}>
              Staff
            </button>
            <button type="button" aria-pressed={tab === "settings"}
              onClick={() => setTab("settings")}>
              Settings
            </button>
          </div>
          {tab === "customers" && !open && admin.can_manage && (
            <button className="btn btn--primary btn--compact"
              onClick={() => setOnboardOpen(true)}>
              Onboard a customer
            </button>
          )}
        </div>

        {tab === "health" && health && <HealthPanel health={health} />}
        {tab === "staff" && <StaffPanel admin={admin} />}
        {tab === "settings" && <SettingsPanel admin={admin} />}
        {tab === "customers" && (
          open
            ? <Detail id={open} canManage={admin.can_manage}
                onBack={() => { setOpen(null); refresh(); }} />
            : <CustomerList customers={customers} onOpen={setOpen} />
        )}
      </main>

      <OnboardDialog open={onboardOpen} onClose={() => setOnboardOpen(false)}
        onCreated={refresh} />
      <PasswordDialog open={passwordOpen || forced} forced={forced}
        onClose={() => setPasswordOpen(false)}
        onDone={() => {
          setPasswordOpen(false);
          setAdmin({ ...admin, must_change_password: false });
        }} />
    </div>
  );
}
